import { hasXSSCharacters, isEmail, isFullNameValid } from "../../../helpers/Utils";
import ICountry from "../../Country.interface";

export interface IReviewAndPaySenderInfoStepModel {
    usePersonalDetails: boolean;
    useBusinessDetails: boolean;
    personDetails: IReviewAndPaySenderInfoPersonDetails;
    businessDetails: IReviewAndPaySenderInfoBusinessDetails;
}

export class ReviewAndPaySenderInfoStepModel implements IReviewAndPaySenderInfoStepModel {
    usePersonalDetails: boolean;
    useBusinessDetails: boolean;
    personDetails: IReviewAndPaySenderInfoPersonDetails;
    businessDetails: IReviewAndPaySenderInfoBusinessDetails;

    constructor() {
        this.usePersonalDetails = false;
        this.useBusinessDetails = false;
        this.personDetails = new ReviewAndPaySenderInfoPersonDetails();
        this.businessDetails = new ReviewAndPaySenderInfoBusinessDetails();
    }

    static isValid(model: IReviewAndPaySenderInfoStepModel, countries: ICountry[]): boolean {
        if (model.usePersonalDetails) {
            return ReviewAndPaySenderInfoPersonDetails.isValid(model.personDetails);
        }

        if (model.useBusinessDetails) {
            return ReviewAndPaySenderInfoBusinessDetails.isValid(model.businessDetails, countries);
        }

        return false;
    }
}

interface IReviewAndPaySenderInfoPersonDetails {
    fullName: string;
    email: string;
}

class ReviewAndPaySenderInfoPersonDetails implements IReviewAndPaySenderInfoPersonDetails {
    fullName: string;
    email: string;

    constructor() {
        this.fullName = "";
        this.email = "";
    }

    static isValid(model: IReviewAndPaySenderInfoPersonDetails): boolean {
        if (!model.fullName || !model.fullName.trim() || hasXSSCharacters(model.fullName)) {
            return false;
        }

        if (!isFullNameValid(model.fullName.trim())) {
            return false;
        }

        if (!model.email || !model.email.trim() || !isEmail(model.email)) {
            return false;
        }

        return true;
    }
}

interface IReviewAndPaySenderInfoBusinessDetails {
    fullName: string;
    email: string;
    companyName: string;
    tradingAs: string;
    businessType: string;
    vat: string;
    cro: string;
    businessAddress: IReviewAndPaySenderInfoBusinessAddress;
}

class ReviewAndPaySenderInfoBusinessDetails implements IReviewAndPaySenderInfoBusinessDetails {
    fullName: string;
    email: string;
    companyName: string;
    tradingAs: string;
    businessType: string;
    vat: string;
    cro: string;
    businessAddress: IReviewAndPaySenderInfoBusinessAddress;

    constructor() {
        this.fullName = "";
        this.email = "";
        this.companyName = "";
        this.tradingAs = "";
        this.businessType = "";
        this.vat = "";
        this.cro = "";
        this.businessAddress = new ReviewAndPaySenderInfoBusinessAddress();
    }

    static isValid(model: IReviewAndPaySenderInfoBusinessDetails, countries: ICountry[]): boolean {
        if (!model.fullName || !model.fullName.trim() || hasXSSCharacters(model.fullName)) {
            return false;
        }

        if (!isFullNameValid(model.fullName)) {
            return false;
        }

        if (!model.email || !model.email.trim() || !isEmail(model.email)) {
            return false;
        }

        if (!model.companyName || !model.companyName.trim() || hasXSSCharacters(model.companyName)) {
            return false;
        }

        if (!model.businessType || !model.businessType.trim() || hasXSSCharacters(model.businessType)) {
            return false;
        }

        if (hasXSSCharacters(model.tradingAs) || hasXSSCharacters(model.vat) || hasXSSCharacters(model.cro)) {
            return false;
        }

        if (!model.businessAddress) {
            return false;
        }

        const isBusinessAddressValid = ReviewAndPaySenderInfoBusinessAddress.isValid(model.businessAddress, countries);
        if (!isBusinessAddressValid) {
            return false;
        }

        return true;
    }
}

interface IReviewAndPaySenderInfoBusinessAddress {
    country: string;
    county: string;
    city: string;
    postcode: string;
    addressLine1: string;
    addressLine2: string;
    addressLine3: string;
}

class ReviewAndPaySenderInfoBusinessAddress implements IReviewAndPaySenderInfoBusinessAddress {
    country: string;
    county: string;
    city: string;
    postcode: string;
    addressLine1: string;
    addressLine2: string;
    addressLine3: string;

    constructor() {
        this.country = '';
        this.county = '';
        this.city = '';
        this.postcode = '';
        this.addressLine1 = '';
        this.addressLine2 = '';
        this.addressLine3 = '';
    }

    static isValid(model: IReviewAndPaySenderInfoBusinessAddress, countries: ICountry[]): boolean {
        if (!model.country || !model.country.trim() || hasXSSCharacters(model.country)) {
            return false;
        }

        if (!model.city || !model.city.trim() || hasXSSCharacters(model.city)) {
            return false;
        }

        if (!model.addressLine1 || !model.addressLine1.trim() || hasXSSCharacters(model.addressLine1)) {
            return false;
        }

        if (model.postcode && model.postcode.trim() && model.postcode.trim().length < 3) {
            return false;
        }

        if (hasXSSCharacters(model.county) ||
            hasXSSCharacters(model.postcode) ||
            hasXSSCharacters(model.addressLine2) ||
            hasXSSCharacters(model.addressLine3)
        ) {
            return false;
        }

        if (countries && !countries.map(c => c.code).includes(model.country)) {
            return false;
        }

        return true;
    }
}